import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type RecallPreview, type RecallRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

/**
 * Pulling a batch out of the field.
 *
 * ── THE MOST CONSEQUENTIAL BUTTON IN THE CONSOLE ────────────────────────────
 *
 * Issuing a recall takes stock off sale at every pharmacy holding the batch and
 * sends a message to every buyer who was ever dispensed it. There is no undo
 * for a message already in somebody's hand, and a careless one teaches people
 * to ignore the next — which is the actual danger, because the next one may be
 * the one that matters.
 *
 * So the screen is built around the PREVIEW rather than the form. Nobody
 * presses a button that messages four hundred people without being shown that
 * it is four hundred people, and which pharmacies, and how many packs.
 *
 * ── WHY CHICARE CAN DO THIS AT ALL ──────────────────────────────────────────
 *
 * Because order items snapshot the batch at the moment of sale (§9.16), kept so
 * a buyer could check the pack against their order at the door. It means this
 * platform can answer a question almost nothing else in the market can: not
 * "which pharmacies might have this" but "which PEOPLE have it at home".
 */
export function RecallsScreen() {
  const [issuing, setIssuing] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'recalls'],
    queryFn: ({ signal }) => api.recalls(signal),
  });

  if (isError) {
    return <AdminError title="We couldn't load the recalls" error={error} onRetry={() => refetch()} />;
  }

  if (issuing) {
    return <IssueRecall onDone={() => setIssuing(false)} />;
  }

  const recalls = data?.recalls ?? [];

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Recalls</h1>
          <p className="m-0 mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
            Pull a batch from every shelf that holds it, and tell every buyer who was sent one.
            Stock comes off sale immediately; the people take longer, which is why they are told
            first and chased after.
          </p>
        </div>

        <Button onClick={() => setIssuing(true)}>Recall a batch</Button>
      </header>

      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-[62px] w-full" radius="card" />
          ))}
        </div>
      ) : recalls.length === 0 ? (
        <EmptyState
          title="Nothing has been recalled"
          body="If NAFDAC or a manufacturer reports a bad batch, this is where it is pulled — from every pharmacy at once."
          action={<Button onClick={() => setIssuing(true)}>Recall a batch</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-card border border-line bg-white">
          {recalls.map((one) => (
            <RecallRowItem key={one.reference} recall={one} />
          ))}
        </div>
      )}
    </div>
  );
}

function RecallRowItem({ recall }: { recall: RecallRow }) {
  const outstanding = recall.pharmacies_notified - recall.acknowledged;

  return (
    <div
      data-testid={`recall-${recall.reference}`}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3.5 py-2.5 last:border-b-0"
    >
      <span className="min-w-0 flex-1 basis-[200px]">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-[13.5px] font-bold text-ink">{recall.product}</span>
          <span
            className={cn(
              'rounded-pill px-2 py-[2px] text-[10.5px] font-bold',
              recall.severity === 'critical'
                ? 'bg-danger-tint text-danger'
                : 'bg-caution-tint text-caution-ink',
            )}
          >
            {recall.severity_label}
          </span>
        </span>
        <span className="mt-0.5 block font-mono text-[11.5px] text-muted">
          batch {recall.batch_no} · {recall.reference}
        </span>
      </span>

      <span className="shrink-0 text-[12px] text-muted lg:w-[118px]">
        <b className="font-mono text-ink">{recall.units_quarantined}</b> packs pulled
      </span>

      <span className="shrink-0 text-[12px] text-muted lg:w-[118px]">
        <b className="font-mono text-ink">{recall.buyers_notified}</b> buyers told
      </span>

      <span className="shrink-0 lg:w-[150px]">
        {recall.closed_at ? (
          <span className="rounded-pill bg-paper px-2 py-[3px] text-[11px] font-medium text-muted">
            Closed
          </span>
        ) : outstanding > 0 ? (
          <span className="rounded-pill bg-caution-tint px-2 py-[3px] text-[11px] font-bold text-caution-ink">
            {outstanding} not confirmed
          </span>
        ) : (
          <span className="rounded-pill bg-verified-tint px-2 py-[3px] text-[11px] font-bold text-verified-ink">
            All shelves clear
          </span>
        )}
      </span>
    </div>
  );
}

// ── Issuing one ─────────────────────────────────────────────────────────────

function IssueRecall({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();

  const [productId, setProductId] = useState('');
  const [batchNo, setBatchNo] = useState('');
  const [sector, setSector] = useState('human');
  const [severity, setSeverity] = useState('critical');
  const [reason, setReason] = useState('');
  const [source, setSource] = useState('');
  const [preview, setPreview] = useState<RecallPreview | null>(null);

  const look = useMutation({
    mutationFn: () =>
      api.previewRecall({
        product_id: Number(productId),
        batch_no: batchNo.trim(),
        sector,
      }),
    onSuccess: setPreview,
  });

  const issue = useMutation({
    mutationFn: () =>
      api.issueRecall({
        product_id: Number(productId),
        batch_no: batchNo.trim(),
        sector,
        severity,
        reason: reason.trim(),
        source: source.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'recalls'] });
      onDone();
    },
  });

  const error = [look.error, issue.error].find((e): e is ApiError => e instanceof ApiError);

  return (
    <div className="mx-auto w-full max-w-[620px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-semibold text-muted"
      >
        <Icon name="back" size={15} strokeWidth={2} />
        All recalls
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">Recall a batch</h1>
      <p className="m-0 mt-1 text-[13px] leading-relaxed text-muted">
        Check what it reaches first. Once issued, the stock is off sale everywhere and the messages
        are gone — there is no undo for something already on somebody’s phone.
      </p>

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="Product ID"
          mono
          value={productId}
          placeholder="1482"
          hint="From the catalogue screen."
          error={error?.fieldErrors.product_id?.[0] ?? null}
          onChange={(event) => {
            setProductId(event.target.value);
            setPreview(null);
          }}
        />

        <Input
          label="Batch number"
          mono
          value={batchNo}
          placeholder="AMX-240817"
          error={error?.fieldErrors.batch_no?.[0] ?? null}
          onChange={(event) => {
            setBatchNo(event.target.value);
            setPreview(null);
          }}
        />

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">Which shop</span>
          <select
            value={sector}
            data-testid="recall-sector"
            onChange={(event) => {
              setSector(event.target.value);
              setPreview(null);
            }}
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
          >
            <option value="human">Pharmacy</option>
            <option value="veterinary">Veterinary</option>
          </select>
        </label>
      </div>

      <Button
        variant="grey"
        className="mt-3"
        disabled={productId === '' || batchNo.trim() === '' || look.isPending}
        onClick={() => look.mutate()}
      >
        {look.isPending ? 'Checking…' : 'Check what this reaches'}
      </Button>

      {/* The screen's whole reason for existing. */}
      {preview ? (
        <div
          data-testid="recall-preview"
          className="mt-4 rounded-card border border-caution bg-caution-tint px-4 py-3.5"
        >
          <p className="m-0 text-[13px] font-bold text-caution-ink">
            This will reach {preview.buyers_to_notify} buyer
            {preview.buyers_to_notify === 1 ? '' : 's'} and {preview.pharmacies.length} pharmac
            {preview.pharmacies.length === 1 ? 'y' : 'ies'}.
          </p>
          <p className="m-0 mt-1 text-[12.5px] leading-snug text-caution-ink">
            {preview.units_on_shelves} pack{preview.units_on_shelves === 1 ? '' : 's'} of{' '}
            <b>{preview.product}</b> batch <span className="font-mono">{preview.batch_no}</span>{' '}
            will come off sale immediately.
          </p>

          {preview.pharmacies.length > 0 ? (
            <ul className="m-0 mt-2 list-none space-y-0.5 p-0">
              {preview.pharmacies.map((one, i) => (
                <li key={i} className="font-mono text-[11.5px] text-caution-ink">
                  {one.name} — {one.units} on shelf
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 mt-1.5 text-[12px] text-caution-ink">
              Nothing is on a shelf. Buyers who already have it will still be told.
            </p>
          )}
        </div>
      ) : null}

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">How serious</span>
          <select
            value={severity}
            data-testid="recall-severity"
            onChange={(event) => setSeverity(event.target.value)}
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
          >
            <option value="critical">Stop using immediately</option>
            <option value="precautionary">Precautionary — stop while we check</option>
          </select>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">
            What is wrong with it?
          </span>
          <textarea
            value={reason}
            rows={3}
            data-testid="recall-reason"
            onChange={(event) => setReason(event.target.value)}
            placeholder="NAFDAC has reported contamination in this batch following testing at the manufacturer."
            className="w-full rounded-inp border border-line bg-white px-3 py-2 text-[13.5px] leading-relaxed text-ink outline-none focus:border-blue"
          />
          <span className="mt-1 block text-[11.5px] leading-snug text-faint">
            This is sent, word for word, to people who have already taken the medicine. Write it for
            them.
          </span>
        </label>

        <Input
          label="Who says so (optional)"
          value={source}
          placeholder="NAFDAC alert 2026/114"
          onChange={(event) => setSource(event.target.value)}
        />
      </div>

      {error ? (
        <p
          role="alert"
          className="m-0 mt-3 rounded-card border border-danger bg-danger-tint px-3.5 py-2.5 text-[12.5px] font-medium text-danger"
        >
          {error.fieldErrors.reason?.[0] ?? error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button
          // Deliberately gated on having looked first. A recall issued without
          // checking what it reaches is how a precautionary notice goes to
          // nine thousand people by accident.
          disabled={preview === null || reason.trim().length < 20 || issue.isPending}
          onClick={() => issue.mutate()}
        >
          {issue.isPending ? 'Issuing…' : 'Issue this recall'}
        </Button>

        <Button variant="grey" onClick={onDone}>
          Cancel
        </Button>
      </div>

      {preview === null ? (
        <p className="m-0 mt-2 text-[11.5px] text-faint">
          Check what it reaches before you can issue it.
        </p>
      ) : null}
    </div>
  );
}
